/**
 * SCR-032 — Nhật ký thao tác (admin only, read-only).
 * Mobile: card list + bottom-sheet filter. Desktop: full table + sidebar drawer.
 */
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Filter, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DataTable } from '@client/components/data-table';
import { useBranches } from '@client/lib/queries/use-branches';
import {
  type AuditLog, type AuditAction, type ListAuditLogsParams,
  useAuditLogs, useAuditLogActors,
} from '@client/lib/queries/use-audit-logs';

export const Route = createFileRoute('/admin/audit-log/')({
  component: AuditLogPage,
});

const ACTION_META: Record<AuditAction, { label: string; variant: 'success' | 'info' | 'destructive' | 'secondary' | 'warning' }> = {
  create: { label: 'Tạo mới', variant: 'success' },
  update: { label: 'Cập nhật', variant: 'info' },
  delete: { label: 'Xóa', variant: 'destructive' },
  login: { label: 'Đăng nhập', variant: 'secondary' },
  share: { label: 'Chia sẻ', variant: 'warning' },
};

const PAGE_SIZE = 25;

function AuditLogPage() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { role?: string } };
  };
  const role = session?.user?.role ?? '';

  // Filters
  const [branchId, setBranchId] = useState('');
  const [actorUserId, setActorUserId] = useState('');
  const [action, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateRangeError, setDateRangeError] = useState('');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  // Detail drawer
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // 403 guard — manager not allowed
  if (role === 'manager') {
    return (
      <Alert variant="destructive">
        <AlertDescription>Bạn không có quyền truy cập nhật ký thao tác.</AlertDescription>
      </Alert>
    );
  }

  const params: ListAuditLogsParams = {
    branchId: branchId || undefined,
    actorUserId: actorUserId || undefined,
    action: action ? (action as AuditAction) : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading, isError, refetch } = useAuditLogs(params);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const { data: actorsData } = useAuditLogActors();

  const branches = branchesData?.items ?? [];
  const actors = actorsData?.items ?? [];

  // api-client unwraps { data: { items, total, ... } } → { items, total, ... }
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  function handleDateChange(field: 'from' | 'to', value: string) {
    const from = field === 'from' ? value : dateFrom;
    const to = field === 'to' ? value : dateTo;
    if (from && to && to < from) {
      setDateRangeError('Đến ngày phải sau hoặc bằng Từ ngày.');
      return;
    }
    setDateRangeError('');
    if (field === 'from') setDateFrom(value);
    else setDateTo(value);
    setPage(1);
  }

  function clearFilters() {
    setBranchId(''); setActorUserId(''); setAction('');
    setDateFrom(''); setDateTo(''); setDateRangeError('');
    setPage(1);
  }

  function openDetail(log: AuditLog) {
    setSelectedLog(log);
    setDetailOpen(true);
  }

  const hasFilters = branchId || actorUserId || action || dateFrom || dateTo;

  const columns: ColumnDef<AuditLog>[] = [
    {
      accessorKey: 'createdAt',
      header: 'Thời gian',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm">
          {format(new Date(row.original.createdAt), 'dd/MM/yyyy HH:mm')}
        </span>
      ),
    },
    {
      accessorKey: 'actorName',
      header: 'Actor',
      cell: ({ row }) => (
        <span title={row.original.actorUserId} className="text-sm">
          {row.original.actorName ?? <span className="text-muted-foreground text-xs">ID: {row.original.actorUserId.slice(0, 8)}…</span>}
        </span>
      ),
    },
    {
      accessorKey: 'action',
      header: 'Hành động',
      cell: ({ row }) => {
        const m = ACTION_META[row.original.action];
        return <Badge variant={m?.variant} className="text-xs">{m?.label ?? row.original.action}</Badge>;
      },
    },
    {
      accessorKey: 'entity',
      header: 'Đối tượng',
      cell: ({ row }) => {
        const { entity, entityId } = row.original;
        return (
          <span className="text-sm">
            {entity}{entityId ? <span className="text-muted-foreground"> #{entityId.slice(0, 8)}</span> : ''}
          </span>
        );
      },
    },
    {
      accessorKey: 'branchName',
      header: 'Cơ sở',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.branchName ?? 'Toàn hệ thống'}</span>
      ),
    },
    {
      id: 'detail',
      header: '',
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => openDetail(row.original)}>
          Chi tiết
        </Button>
      ),
    },
  ];

  const filterPanel = (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Cơ sở</label>
          <Select value={branchId || 'all'} onValueChange={(v) => { setBranchId(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-10"><SelectValue placeholder="Toàn hệ thống" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toàn hệ thống</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Người thực hiện</label>
          <Select value={actorUserId || 'all'} onValueChange={(v) => { setActorUserId(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-10"><SelectValue placeholder="Tất cả" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả</SelectItem>
              {actors.map((a) => <SelectItem key={a.userId} value={a.userId}>{a.fullName}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Hành động</label>
          <Select value={action || 'all'} onValueChange={(v) => { setAction(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-10"><SelectValue placeholder="Tất cả" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả</SelectItem>
              {Object.entries(ACTION_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Từ ngày</label>
          <Input type="date" value={dateFrom} onChange={(e) => handleDateChange('from', e.target.value)} className="h-10" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Đến ngày</label>
          <Input type="date" value={dateTo} onChange={(e) => handleDateChange('to', e.target.value)} className="h-10" />
        </div>
      </div>
      {dateRangeError && <p className="text-xs text-destructive">{dateRangeError}</p>}
      {hasFilters && (
        <Button variant="ghost" size="sm" className="self-start h-9 text-xs gap-1" onClick={clearFilters}>
          <X className="h-3.5 w-3.5" /> Xóa bộ lọc
        </Button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-foreground">Nhật ký thao tác</h1>
        <div className="flex gap-2">
          {/* Mobile filter toggle */}
          <Button variant="outline" size="sm" className="md:hidden h-10 gap-1" onClick={() => setFilterOpen(true)}>
            <Filter className="h-4 w-4" /> Bộ lọc {hasFilters && <span className="bg-primary text-primary-foreground rounded-full text-[10px] w-4 h-4 flex items-center justify-center">!</span>}
          </Button>
          <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => void refetch()} title="Làm mới">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Desktop filters — hidden on mobile */}
      <div className="hidden md:block">{filterPanel}</div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage={hasFilters ? 'Không có nhật ký nào khớp bộ lọc.' : 'Chưa có nhật ký nào.'}
        pagination={total > 0 ? { page, pageSize: PAGE_SIZE, total } : undefined}
        onPageChange={setPage}
      />

      {/* Mobile card list */}
      {!isLoading && !isError && items.length > 0 && (
        <div className="flex flex-col gap-2 md:hidden">
          {items.map((log) => {
            const m = ACTION_META[log.action];
            return (
              <button
                key={log.id}
                className="text-left w-full p-3 rounded-md border border-border bg-background hover:bg-muted/50 transition-colors"
                onClick={() => openDetail(log)}
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge variant={m?.variant} className="text-xs">{m?.label ?? log.action}</Badge>
                  <span className="text-xs text-muted-foreground">{format(new Date(log.createdAt), 'dd/MM HH:mm')}</span>
                </div>
                <p className="text-sm font-medium mt-1">{log.actorName ?? log.actorUserId.slice(0, 12)}</p>
                <p className="text-xs text-muted-foreground">
                  {log.entity}{log.entityId ? ` #${log.entityId.slice(0, 8)}` : ''} · {log.branchName ?? 'Toàn hệ thống'}
                </p>
              </button>
            );
          })}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2 pt-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Trước</Button>
              <span className="text-sm text-muted-foreground">Trang {page}/{totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Sau ›</Button>
            </div>
          )}
        </div>
      )}

      {/* Mobile filter sheet */}
      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Bộ lọc</SheetTitle>
          </SheetHeader>
          {filterPanel}
          <div className="pt-4">
            <Button className="w-full" onClick={() => setFilterOpen(false)}>Áp dụng</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail drawer */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Chi tiết nhật ký</SheetTitle>
          </SheetHeader>
          {selectedLog && (
            <dl className="flex flex-col gap-3 text-sm">
              <LogField label="ID" value={selectedLog.id} mono />
              <LogField label="Thời gian" value={format(new Date(selectedLog.createdAt), 'dd/MM/yyyy HH:mm:ss')} />
              <LogField label="Actor" value={selectedLog.actorName ?? undefined} />
              <LogField label="Actor ID" value={selectedLog.actorUserId} mono />
              <LogField label="Hành động" value={ACTION_META[selectedLog.action]?.label ?? selectedLog.action} />
              <LogField label="Đối tượng" value={selectedLog.entity} />
              <LogField label="Entity ID" value={selectedLog.entityId ?? undefined} mono />
              <LogField label="Cơ sở" value={selectedLog.branchName ?? 'Toàn hệ thống'} />
              <div>
                <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Meta</dt>
                <dd className="rounded-md bg-muted p-3 overflow-auto max-h-64">
                  {selectedLog.meta
                    ? <pre className="text-xs font-mono whitespace-pre-wrap break-all">{JSON.stringify(selectedLog.meta, null, 2)}</pre>
                    : <span className="text-muted-foreground text-xs">Không có dữ liệu meta</span>
                  }
                </dd>
              </div>
            </dl>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function LogField({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className={`mt-0.5 text-sm break-all ${mono ? 'font-mono' : ''}`}>
        {value ?? <span className="text-muted-foreground/60">—</span>}
      </dd>
    </div>
  );
}
